import { useState, useEffect } from 'react';

interface HelpInfoBoxReturn {
  showInfo: boolean;
  handleDismiss: () => void;
  handleToggle: () => void;
  shouldShow: boolean;
}

function readStored(dismissKey: string): {
  count: number;
  last: number | null;
} {
  if (typeof window === 'undefined') {
    return { count: 0, last: null };
  }
  try {
    const data = JSON.parse(localStorage.getItem(dismissKey) || '{}');
    return {
      count: data.count ?? 0,
      last: data.last ?? null,
    };
  } catch {
    return { count: 0, last: null };
  }
}

function persistDismiss(dismissKey: string): void {
  const { count } = readStored(dismissKey);
  localStorage.setItem(
    dismissKey,
    JSON.stringify({ count: count + 1, last: Date.now() })
  );
}

const useHelpInfoBox = (dismissKey: string): HelpInfoBoxReturn => {
  const [showInfo, setShowInfo] = useState<boolean>(() => {
    const { count, last } = readStored(dismissKey);
    if (count >= 3) {
      return false;
    }
    if (last && Date.now() - last < 24 * 60 * 60 * 1000) {
      return false;
    }
    return true;
  });
  const [dismissCount, setDismissCount] = useState<number>(
    () => readStored(dismissKey).count
  );

  useEffect(() => {
    const { count, last } = readStored(dismissKey);
    if (count >= 3 || (last && Date.now() - last < 24 * 60 * 60 * 1000)) {
      setShowInfo(false);
    }
    setDismissCount(count);
  }, [dismissKey]);

  const handleDismiss = (): void => {
    persistDismiss(dismissKey);
    setDismissCount((prev) => prev + 1);
    setShowInfo(false);
  };

  const handleToggle = (): void => {
    setShowInfo((prev) => {
      const next = !prev;
      if (!next) {
        persistDismiss(dismissKey);
      }
      return next;
    });
    setDismissCount(readStored(dismissKey).count);
  };

  const shouldShow = (): boolean => {
    return showInfo && dismissCount < 3;
  };

  return {
    showInfo,
    handleDismiss,
    handleToggle,
    shouldShow: shouldShow(),
  };
};

export default useHelpInfoBox;
